import React from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { getInsightBySlug } from "../../data/insights";

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function InsightArticlePage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getInsightBySlug(slug) : undefined;

  if (!article) {
    return (
      <section className="min-h-[60vh] pt-40 pb-20 px-[5%] text-center">
        <h1 className="font-teko text-5xl text-text-obsidian mb-4">
          Article not found
        </h1>
        <p className="font-manrope text-text-slate mb-8">
          We could not find that article. It may have been moved or removed.
        </p>
        <Link
          to="/insights"
          className="font-teko text-xl uppercase text-brand-yellow border-b border-brand-yellow"
        >
          Back to Insights
        </Link>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} | Letting Engine Insights</title>
        <meta name="description" content={article.excerpt} />
      </Helmet>

      <article className="pt-40 pb-24 px-[5%]">
        <div className="max-w-[800px] mx-auto">
          <Link
            to="/insights"
            className="font-manrope text-sm text-text-slate hover:text-brand-yellow transition-colors mb-8 inline-block"
          >
            ← Back to Insights
          </Link>

          <time
            dateTime={article.date}
            className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-4"
          >
            {formatDate(article.date)}
          </time>

          <h1 className="font-teko text-5xl md:text-6xl text-text-obsidian leading-tight mb-8 tracking-[-1px]">
            {article.title}
          </h1>

          <p className="font-manrope text-lg text-text-slate leading-relaxed border-l-4 border-l-solid border-l-brand-yellow pl-6 mb-12">
            {article.excerpt}
          </p>

          <div className="font-manrope text-base leading-relaxed text-text-slate space-y-6">
            {article.body.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-t-solid border-t-[rgba(0,0,0,0.1)]">
            <p className="font-manrope text-sm text-text-slate mb-4">
              Looking for a luxury rental or advice on letting your property?
            </p>
            <Link
              to="/contact-us"
              className="font-teko text-xl uppercase text-brand-yellow border-b border-brand-yellow hover:text-text-obsidian hover:border-text-obsidian transition-colors"
            >
              Get in touch
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
